/**
 * 🌙 DARK THEME
 *
 * Темная тема с контрастными цветами.
 * Полностью самодостаточна, содержит все цвета напрямую.
 *
 * Архитектура: все цвета определены непосредственно в теме,
 * адаптированы для темного интерфейса.
 */

import type { ThemeTokens } from '../semantic';
import { createThemeWithDerivations } from '../color-derivations';

const baseDarkTheme = {
  // ============================================
  // 📐 SURFACES (поверхности)
  // ============================================
  bg: '#0F0F0F',                          // Темный фон
  surface: '#1A1A1A',                     // Темная поверхность
  card: '#2A2A2A',                        // Темные карточки
  elevated: '#333333',                    // Приподнятые элементы

  // ============================================
  // 📝 TEXT — спокойные оттенки, без чисто белого
  // ============================================
  text: '#E0E0E0',                        // Мягкий светлый (не белый)
  textMuted: '#9E9E9E',                   // Вторичный текст потише
  textTertiary: '#7A7A7A',                // Третичный
  textPlaceholder: '#6B6B6B',             // Плейсхолдеры

  // ============================================
  // 🟡 PRIMARY (основной акцент) — приглушённые для консистентности с тёмным фоном
  // ============================================
  primary: '#C9A000',                     // Тёмный янтарь (не неон)
  onPrimary: '#1B1B1B',                   // Темный текст на акценте
  link: '#D4B84A',                        // Приглушённая ссылка
  onLightSurface: '#1B1B1B',              // Темный текст на светлых плашках (статусы, карточки)

  // ============================================
  // 📊 BORDERS (границы)
  // ============================================
  border: 'rgba(255, 255, 255, 0.10)',    // Мягкий белый с альфой (как в Material/Yandex)
  borderStrong: 'rgba(255, 255, 255, 0.16)', // Более контрастные границы

  // ============================================
  // ✅ SEMANTIC COLORS (семантические цвета)
  // ============================================
  success: '#4E8A67',
  onSuccess: '#0B0B0B',                   // Контрастный черный для WCAG AA
  warning: '#B9823A',
  onWarning: '#0B0B0B',                   // Контрастный черный для WCAG AA
  error: '#C85C5C',
  onError: '#0B0B0B',                     // Контрастный черный для WCAG AA
  info: '#4E7FA8',
  onInfo: '#0B0B0B',                      // Контрастный черный для WCAG AA

  // ============================================
  // 🎯 INTERACTIVE (интерактивные состояния)
  // ============================================
  focus: 'rgba(201, 160, 0, 0.28)',        // Кольцо фокуса от primary (#C9A000)
  disabled: '#6B6B6B',                     // Отключенный текст (самый "тухлый", но читаемый ~3.3:1 на surface)

  // ============================================
  // 🌑 SCRIM (затемнение)
  // ============================================
  scrim: 'rgba(0, 0, 0, 0.05)',            // Заблюренный чёрный 5%

  // ============================================
  // 📜 SCROLLBAR (полоса прокрутки графика аналитики)
  // ============================================
  scrollbarTrack: '#1A1A1A',   // surface
  scrollbarThumb: 'rgba(255, 255, 255, 0.10)',   // border
  scrollbarThumbHover: '#9E9E9E',                 // textMuted (консистентно с текстом)

  // ============================================
  // 📅 APPOINTMENT STATUSES — приглушённые пастели под тёмный фон
  // ============================================
  apptScheduledBg: '#5C7A9E',   // Приглушённый синий
  apptCompletedBg: '#6B8F6B',   // Приглушённый зелёный
  apptNoShowBg: '#9A7B5A',      // Приглушённый оранжевый
  apptCancelledBg: '#9A5C5C',  // Приглушённый красный
  apptMovedBg: '#7A6B8A',      // Приглушённый фиолетовый
  apptBreakBg: '#6B737A',      // Приглушённый серо-голубой
};

const derived = createThemeWithDerivations(baseDarkTheme, 'dark');
/**
 * Primary «container» для выделений: низкая яркость/хрома — не с неоновой плашкой.
 * onPrimaryContainer для dark — светлый (см. color-derivations).
 */
export const darkTheme: ThemeTokens = {
  ...derived,
  primaryLight: '#3F3C36',
};